import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Sparkles, Sun, Share2, Heart } from "lucide-react";
import { getSundayExhortation, getNextSundayDate } from "@/lib/sunday-data";
import VerseCard from "@/components/VerseCard";

const STUDIO = "/assets/studio-team.png";

export default function Sunday() {
  const item = getSundayExhortation();
  const [cardOpen, setCardOpen] = useState(false);
  const nextSunday = getNextSundayDate();
  const isToday = new Date().getDay() === 0;

  const dateLabel = nextSunday.toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div data-testid="page-sunday" className="bg-[#F7F5F0]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={STUDIO} alt="" className="w-full h-full object-cover jt-pan opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2E4959] via-[#4A6D82] to-[#7B98A8]" />
          <div className="absolute -left-32 top-20 w-96 h-96 rounded-full bg-[#C99A5B]/25 blur-3xl" />
          <div className="absolute -right-32 bottom-10 w-96 h-96 rounded-full bg-[#F4E4C1]/15 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 pt-32 pb-16 md:pt-40 md:pb-24 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-[11px] uppercase tracking-[0.3em] mb-6">
            <Sun className="w-3 h-3" strokeWidth={2.5} /> {isToday ? "Aujourd'hui" : dateLabel}
          </span>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] text-white mb-4">
            Méditation<br />du <em className="not-italic text-[#F4E4C1]">dimanche</em>.
          </h1>
          <p className="text-lg text-white/85 max-w-2xl mx-auto">
            Chaque semaine, un verset et une exhortation choisis pour t'accompagner dans ta marche.
          </p>
        </div>
      </section>

      {/* Verse card centerpiece */}
      <section className="relative -mt-16 pb-4 max-w-4xl mx-auto px-6">
        <div className="relative rounded-3xl bg-white border border-[#DDE3E8] p-6 md:p-10 jt-medium-shadow">
          <div className="flex items-center justify-between mb-8">
            <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82] flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> Verset du dimanche
            </span>
            <button
              onClick={() => setCardOpen(true)}
              data-testid="sunday-card-open"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E4EBEF] text-[#4A6D82] text-xs hover:bg-[#4A6D82] hover:text-white transition"
            >
              <Share2 className="w-3.5 h-3.5" /> Créer une carte
            </button>
          </div>

          <blockquote className="font-serif text-3xl md:text-5xl leading-[1.15] text-[#1E2933] mb-6" data-testid="sunday-verse">
            <span className="text-5xl md:text-6xl text-[#C99A5B] leading-none">«</span> {item.verse.text} <span className="text-5xl md:text-6xl text-[#C99A5B] leading-none">»</span>
          </blockquote>

          <p className="font-serif italic text-xl text-[#4A6D82]">— {item.verse.ref}</p>
        </div>
      </section>

      {/* Exhortation */}
      <section className="max-w-4xl mx-auto px-6 py-16 md:py-24" data-testid="sunday-exhortation">
        <div className="mb-10 text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-[#C99A5B] flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {item.theme}
          </span>
          <h2 className="font-serif text-4xl md:text-6xl text-[#1E2933] mt-3 leading-[1.05]">
            {item.title}
          </h2>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-lg md:text-xl text-[#1E2933]/85 leading-[1.75] first-letter:text-6xl first-letter:font-serif first-letter:text-[#4A6D82] first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9]">
            {item.exhortation}
          </p>
        </div>

        {/* Action of the week */}
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-[#4A6D82] to-[#2E4959] text-white p-8 md:p-10 jt-medium-shadow relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-[#C99A5B]/25 blur-2xl" />
          <div className="absolute -left-12 -bottom-12 w-48 h-48 rounded-full bg-[#F4E4C1]/10 blur-2xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#F4E4C1] mb-4">
              <Heart className="w-3.5 h-3.5" /> Pas concret de la semaine
            </span>
            <p className="font-serif text-2xl md:text-3xl leading-tight">{item.action}</p>
          </div>
        </div>
      </section>

      {/* Actions & links */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82]">Prolonger le dimanche</span>
          <h3 className="font-serif text-3xl md:text-4xl text-[#1E2933] mt-3 mb-8">Va plus loin cette semaine</h3>

          <div className="grid gap-4 md:grid-cols-3">
            <Link to="/podcasts" data-testid="sunday-link-podcasts" className="group rounded-2xl bg-[#F7F5F0] border border-[#DDE3E8] p-6 hover:border-[#4A6D82] transition text-left">
              <p className="text-xs uppercase tracking-widest text-[#C99A5B] mb-2">Écoute</p>
              <p className="font-serif text-xl text-[#1E2933] mb-3">Un podcast pour méditer</p>
              <span className="inline-flex items-center gap-1.5 text-sm text-[#4A6D82] group-hover:gap-2 transition-all">
                Voir les épisodes <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/ressources" data-testid="sunday-link-resources" className="group rounded-2xl bg-[#F7F5F0] border border-[#DDE3E8] p-6 hover:border-[#4A6D82] transition text-left">
              <p className="text-xs uppercase tracking-widest text-[#C99A5B] mb-2">Médite</p>
              <p className="font-serif text-xl text-[#1E2933] mb-3">Télécharge une fiche</p>
              <span className="inline-flex items-center gap-1.5 text-sm text-[#4A6D82] group-hover:gap-2 transition-all">
                Ouvrir les ressources <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/temoignages" data-testid="sunday-link-testimonies" className="group rounded-2xl bg-[#F7F5F0] border border-[#DDE3E8] p-6 hover:border-[#4A6D82] transition text-left">
              <p className="text-xs uppercase tracking-widest text-[#C99A5B] mb-2">Partage</p>
              <p className="font-serif text-xl text-[#1E2933] mb-3">Ton histoire compte</p>
              <span className="inline-flex items-center gap-1.5 text-sm text-[#4A6D82] group-hover:gap-2 transition-all">
                Envoyer un témoignage <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {cardOpen && <VerseCard verse={item.verse} onClose={() => setCardOpen(false)} />}
    </div>
  );
}
